import { useState, useEffect, useMemo, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Typography,
  Checkbox,
  Autocomplete,
  Chip,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import isEmpty from "lodash/isEmpty";

import { specialitySchema } from "../../yup";
import Input from "../../components/UserInput/Input";
import {
  useUpdateSpecialityDetailsMutation,
  useGetClinicalDiagramsQuery,
  useGetSpecialityDetailQuery,
} from "../../services/api";
import style from "./speciality.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import { getErrorMessage } from "../../utils/common";

type FormState = typeof specialitySchema.__outputType;
type ClinicalDiagramOption = {
  id: number;
  label: string;
  value: string;
};

/**
 * Edit Speciality page for updating an existing speciality.
 *
 * Notes:
 * - Loads speciality details and clinical diagram options before reset.
 * - Validation is enforced via `specialitySchema`.
 */
const EditSpeciality = () => {
  const [updateSpecialityDetails] = useUpdateSpecialityDetailsMutation();
  const { id } = useParams();
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const { data: clinicalDiagramData, isLoading: isClinicalDiagramLoading } =
    useGetClinicalDiagramsQuery({
    order_by_columns: "name",
    order: "Asc",
    is_detailed: true,
    search_key: "",
  });

  const clinicalDiagrams = useMemo<ClinicalDiagramOption[]>(
    () =>
      clinicalDiagramData?.items?.map((clinicalDiagram: ClinicalDiagram) => {
        return {
          id: clinicalDiagram?.id,
          label: clinicalDiagram?.name,
          value: clinicalDiagram?.name,
        };
      }) ?? [],
    [clinicalDiagramData]
  );

  const {
    data: specialityDetails,
    isLoading,
    isFetching,
  } = useGetSpecialityDetailQuery(id ?? "");

  const navigation = useNavigate();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [isSavingSpeciality, setIsSavingSpeciality] = useState(false);
  const clinicalDiagramDetails = useMemo<ClinicalDiagram[]>(
    () =>
      // Map stored ids to full objects; filter out missing entries to keep form values consistent.
      specialityDetails?.clinical_diagram_ids
        ?.map((diagramId) =>
          clinicalDiagramData?.items?.find(
            (clinicalDiagram: ClinicalDiagram) =>
              clinicalDiagram.id === diagramId
          )
        )
        .filter(
          (diagram): diagram is ClinicalDiagram => Boolean(diagram)
        ) ?? [],
    [clinicalDiagramData, specialityDetails]
  );

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(specialitySchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    reset,
  } = useForm<FormState>({
    resolver: yupResolver(specialitySchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (!isEmpty(specialityDetails)) {
      // Reset form state from server data to avoid stale edits when navigating between records.
      reset({
        name: specialityDetails?.name,
        clinical_diagram_ids: clinicalDiagramDetails?.map(
          (diagram: ClinicalDiagram) => {
            return {
              id: diagram?.id,
              label: diagram?.name,
              value: diagram?.name,
            };
          }
        ),
      });
    }
  }, [clinicalDiagramDetails, reset, specialityDetails]);
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Submits the updated speciality payload.
   *
   * Args:
   *   values (FormState): Form values for the speciality update.
   *
   * Returns:
   *   Promise<void>
   *
   * Raises:
   *   Error: Propagated when the update mutation fails.
   *
   * Notes:
   * - Uses route `id`; defaults to `0` if missing.
   * - TODO: Confirm expected behavior when `id` is missing or invalid.
   */
  const onSubmit = async (values: FormState) => {
    try {
      const parsedId = parseInt(id ?? "", 10);
      if (Number.isNaN(parsedId)) {
        setMessage({
          type: "error",
          text: "Unable to update speciality. Please reload and try again.",
        });
        return;
      }
      setIsSavingSpeciality(true);
      const { name, clinical_diagram_ids } = values;
      const payload: EditSpecialityPayload = {
        id: parsedId,
        name: name,
        clinical_diagram_ids:
          clinical_diagram_ids?.map((clinicalDiagram) => clinicalDiagram.id) ??
          [],
      };

      await updateSpecialityDetails(payload).unwrap();
      setIsSavingSpeciality(false);
      navigationTimeoutRef.current = setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Please enter valid details"),
      });
      setIsSavingSpeciality(false);
    }
  };

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {(isFetching || isLoading || isClinicalDiagramLoading) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
              width: "calc(100vw - 400px)",
              zIndex: 999,
              backgroundColor: "transparent",
              position: "absolute",
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        <Box>
          <Typography
            className={style.detailHeading}
            sx={{ marginTop: "10px" }}
          >
            Speciality Details
          </Typography>
          <Box className={style.flexTop}>
            <Box className={style.row}>
              <Input
                placeholder="Enter name"
                label="Speciality Name"
                required
                register={register}
                name="name"
                errors={errors}
              />
            </Box>
            <Box className={style.row} sx={{ flexDirection: "column" }}>
              <Typography
                className={style.inputLabel}
                sx={{ marginBottom: -2 }}
              >
                Clinical Diagram Mapping
              </Typography>
              <Controller
                control={control}
                name={"clinical_diagram_ids"}
                rules={{ required: false }}
                render={({ field: { onChange, value }, ...props }) => (
                  <Autocomplete<ClinicalDiagramOption, true, true, false>
                    {...props}
                    sx={{
                      width: "100%",
                    }}
                    multiple
                    disablePortal
                    disableCloseOnSelect
                    id="tags-outlined"
                    options={clinicalDiagrams}
                    disableClearable={true}
                    onChange={(e, data) => onChange(data)}
                    getOptionLabel={(option: ClinicalDiagramOption) =>
                      option.label
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    value={value ?? []}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder={
                          isEmpty(getValues("clinical_diagram_ids"))
                            ? "Select clinical diagrams"
                            : ""
                        }
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true,
                        }}
                      />
                    )}
                    renderTags={(
                      value: readonly ClinicalDiagramOption[],
                      getTagProps
                    ) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option.label}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.label}
                      </li>
                    )}
                  />
                )}
              />
            </Box>
          </Box>
          {message && (
            <Box mt={2}>
              <LabelBox
                boxProps={{ height: 50, display: "flex", alignItems: "center" }}
                data={message}
                onClose={() => setMessage(null)}
              />
            </Box>
          )}
          <Box className={style.buttons}>
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: "transparent" },
              }}
              variant="outlined"
              onClick={() => navigation(-1)}
              label={"Back"}
            />
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: colors.primary },
              }}
              variant="contained"
              type="submit"
              label={"Save"}
              loading={isSavingSpeciality}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EditSpeciality;
